import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FileDown, X } from "lucide-react";
import QuickLeadForm from "@/components/QuickLeadForm";
import { TID } from "@/constants/testIds";

/**
 * Floating "Download Brochure" pill that lives bottom-right on every page
 * (hidden on /admin and /eoi). Opens a modal with the QuickLeadForm — on
 * success, the backend emails the PDF and BCCs the sales inbox.
 */
export default function StickyBrochureCTA() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (pathname.startsWith("/admin") || pathname.startsWith("/eoi")) return null;

  return (
    <>
      <button
        data-testid={TID.stickyBrochureOpen}
        onClick={() => setOpen(true)}
        // Sits above the mobile sticky bar on small screens
        className="fixed z-40 bottom-24 md:bottom-6 right-4 md:right-6 inline-flex items-center gap-2 px-4 md:px-5 py-3 rounded-full text-sm font-body bg-forest text-cream shadow-[0_18px_40px_-12px_rgba(11,74,61,0.6)] hover:bg-void transition-all hover:-translate-y-0.5"
        aria-label="Download the Pali Hill City brochure"
      >
        <FileDown size={17} strokeWidth={2.2} />
        <span className="hidden sm:inline">Download Brochure</span>
        <span className="sm:hidden">Brochure</span>
      </button>

      {open && (
        <div
          data-testid={TID.brochureModal}
          className="fixed inset-0 z-[80] bg-void/60 backdrop-blur-sm p-4 flex items-center justify-center animate-in fade-in duration-300"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-offwhite rounded-2xl w-full max-w-[520px] max-h-[92vh] overflow-y-auto shadow-[0_40px_80px_-30px_rgba(11,74,61,0.7)] fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-7 relative">
              <button
                data-testid={TID.brochureModalClose}
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center bg-forest/10 hover:bg-forest text-forest hover:text-cream transition-colors z-10"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div className="eyebrow text-teal">Chapter III · Detailed brochure</div>
              <h3 className="font-display text-2xl md:text-3xl text-forest mt-2 leading-tight pr-8">
                Get the full brochure in your inbox.
              </h3>
              <p className="text-sm text-forest/70 mt-2 leading-relaxed">
                Plot layouts, corridor context, phase status, and paperwork
                clarity — as a PDF, attached to a single email. No follow-up
                blitz.
              </p>
              <div className="mt-5">
                <QuickLeadForm
                  source="brochure_sticky"
                  variant="brochure"
                  onSubmitted={() => setTimeout(() => setOpen(false), 3500)}
                  ctaLabel="Email me the brochure"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
